// PR-3634 in-app trial pop-ups (product manager's flow, 02-Nov): which modal to show on this app load.
// Pure rules + a small localStorage memory per account. "Once" = a reminder is marked seen when it is shown.
import { ProTrialInfo } from '../../Models/Tiers/TierModels';
import { STARTER_TIER_ID } from './proTrialRules';

export const PRO_TIER_ID = 3;
export const REMINDER_STORAGE_PREFIX = 'pulseem.proTrialReminders.';

// welcome = pop-up 1 (first load on the trial, more than 5 days left), endsSoon = pop-up 2 (5..2 days left),
// lastDay = pop-up 3 (last day), ended = pop-up 4 (first load after expiry)
export type ProTrialReminder = 'welcome' | 'endsSoon' | 'lastDay' | 'ended';

// The trial this browser last saw for the account (keyed by its EndDate, so an extension starts fresh) and what was shown.
export interface ProTrialReminderMemory {
  trialEndDate: string;
  seen: ProTrialReminder[];
}

// memory null = remove the stored memory
export interface ProTrialReminderDecision {
  reminder: ProTrialReminder | null;
  memory: ProTrialReminderMemory | null;
}

export const getDueTrialReminder = (daysRemaining: number): ProTrialReminder | null => {
  if (daysRemaining === 1) return 'lastDay';
  if (daysRemaining >= 2 && daysRemaining <= 5) return 'endsSoon';
  if (daysRemaining > 5) return 'welcome';
  return null;
};

// expiredEndDate = GetCurrentPlan ProTrialExpiredEndDate: the API's "this trial expired in the last 30 days".
// Without it (older API) pop-up 4 falls back to what this browser saw during the trial.
export const decideProTrialReminder = (
  info: ProTrialInfo | null,
  currentPlanId: number,
  memory: ProTrialReminderMemory | null,
  now: number,
  expiredEndDate: string | null = null
): ProTrialReminderDecision => {
  // On the trial: remember it, and show pop-up 1, 2 or 3 once per trial period. An extension (new EndDate) starts
  // a new period, so 2 and 3 can show again; the welcome is kept, so an extension does not say "trial started".
  if (info && info.IsOnTrial && info.EndDate) {
    const base: ProTrialReminderMemory =
      memory && memory.trialEndDate === info.EndDate
        ? memory
        : { trialEndDate: info.EndDate, seen: memory && memory.seen.indexOf('welcome') >= 0 ? ['welcome'] : [] };
    const due = getDueTrialReminder(info.DaysRemaining);
    if (due && base.seen.indexOf(due) < 0) return { reminder: due, memory: { ...base, seen: [...base.seen, due] } };
    return { reminder: null, memory: base };
  }

  // Locked but no longer on the trial (end passed, hourly job not run yet): wait for the job
  if (info) return { reminder: null, memory };

  // The API says the trial expired recently: pop-up 4 once per trial, only while the account is on Starter
  // (a plan bought after the expiry means no "trial ended" message).
  if (expiredEndDate) {
    if (currentPlanId !== STARTER_TIER_ID) return { reminder: null, memory: null };
    const base: ProTrialReminderMemory =
      memory && memory.trialEndDate === expiredEndDate ? memory : { trialEndDate: expiredEndDate, seen: [] };
    if (base.seen.indexOf('ended') < 0) return { reminder: 'ended', memory: { ...base, seen: [...base.seen, 'ended'] } };
    return { reminder: null, memory: base };
  }

  // No trial info from the API: decide only from what this browser saw during the trial
  if (!memory) return { reminder: null, memory: null };
  const end = Date.parse(memory.trialEndDate);
  if (isNaN(end)) return { reminder: null, memory: null };

  if (end <= now) {
    // Trial over: any paid tier = the customer bought one; Starter = it expired (pop-up 4, once).
    // 'ended' stays in the memory so a later API answer for the same trial does not show it again.
    if (currentPlanId !== STARTER_TIER_ID) return { reminder: null, memory: null };
    if (memory.seen.indexOf('ended') >= 0) return { reminder: null, memory };
    return { reminder: 'ended', memory: { ...memory, seen: [...memory.seen, 'ended'] } };
  }
  // Closed before its end date: revoked (Starter) or Standard/Scale bought -> forget it.
  // Pro is ambiguous (Pro bought, or the trial status could not be read this time) -> keep it.
  return { reminder: null, memory: currentPlanId === PRO_TIER_ID ? memory : null };
};

// Account part of the storage key: one memory per account, so a shared browser does not mix accounts.
// GetSubAccountWithFeatureAndSettings does not send AccountID/SubAccountId to the browser ([JsonIgnore]), so the
// key falls back to company name + sub-account name. A rare clash only means the memory is shared (keyed by EndDate).
export const getReminderAccountKey = (subAccount: any): string | null => {
  const id = subAccount?.AccountID ?? subAccount?.SubAccountId;
  if (id !== undefined && id !== null && id !== '') return String(id);
  const company = subAccount?.Account?.CompanyName ?? '';
  const name = subAccount?.SubAccountName ?? '';
  return company === '' && name === '' ? null : `${company}|${name}`;
};

type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

const getLocalStorage = (): StorageLike | null => {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch (e) {
    return null;
  }
};

const VALID_REMINDERS: ProTrialReminder[] = ['welcome', 'endsSoon', 'lastDay', 'ended'];

// Never throws; an unreadable or malformed value counts as no memory.
export const readReminderMemory = (
  accountKey: string,
  storage: StorageLike | null = getLocalStorage()
): ProTrialReminderMemory | null => {
  try {
    if (!storage) return null;
    const raw = storage.getItem(REMINDER_STORAGE_PREFIX + accountKey);
    if (raw === null) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.trialEndDate !== 'string' || !Array.isArray(parsed.seen)) return null;
    return {
      trialEndDate: parsed.trialEndDate,
      seen: parsed.seen.filter((r: any) => VALID_REMINDERS.indexOf(r) >= 0),
    };
  } catch (e) {
    return null;
  }
};

// Never throws; null removes the memory.
export const writeReminderMemory = (
  accountKey: string,
  memory: ProTrialReminderMemory | null,
  storage: StorageLike | null = getLocalStorage()
): void => {
  try {
    if (!storage) return;
    if (memory) storage.setItem(REMINDER_STORAGE_PREFIX + accountKey, JSON.stringify(memory));
    else storage.removeItem(REMINDER_STORAGE_PREFIX + accountKey);
  } catch (e) {
    // Storage full or blocked: the reminder still shows this time; it may show again on a later load.
  }
};
